import { useMobxStores } from '@/stores';
import { mapReviewStars } from '@/utils/tools';
import { InfoCircleOutlined } from '@ant-design/icons';
import { faMapMarkerAlt, faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, Rate } from 'antd';
import React, { Fragment } from 'react';

export default function Rating({
  review_grade,
  facilityAddress,
  isBranded,
  reviewsRef,
  locationRef,
  facilityDistance,
  facilityCode,
  highlights,
  unbrandedAddress,
  ...props
}) {
  const { reservationStore } = useMobxStores();

  const checkedHighlights = highlights.filter((highlight) => highlight.check);
  const hasDistanceHighlight = highlights.some(
    (highlight) => highlight.type === 'facility_distance' && highlight.check
  );

  return (
    <>
      <div className="hidden md:flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 mb-4 md:mb-12">
        <div className="flex flex-row bg-gray-50 p-4">
          <div className="flex flex-col">
            <span className={`text-base ml-2`}>
              <span className="text-3xl font-semibold">
                {mapReviewStars(review_grade)}
              </span>{' '}
              / 5
            </span>
            <Rate disabled value={review_grade} allowHalf className="text-sm" />
          </div>
          <div className="flex flex-col">
            <span className={`text-2xl font-semibold`}>
              {review_grade >= 4.5
                ? 'Excellent!'
                : review_grade >= 4.0
                ? 'Very Good!'
                : 'Good!'}
            </span>

            <button
              onClick={() => {
                reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-secondary text-base ml-4 mr-1"
            >
              ({reservationStore.reviewsTotal} Reviews)
            </button>
          </div>
        </div>
        {isBranded == 1 ? (
          <div className="bg-gray-50 p-4 space-y-2">
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                {...props}
                className="w-5 h-5 mr-2"
              />
              <p className={`text-base`}>{facilityAddress}</p>
            </div>
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faPlane}
                {...props}
                className="-rotate-90 w-5 h-5 mr-2"
              />
              {hasDistanceHighlight &&
                checkedHighlights.map((highlight, index) => (
                  <Fragment key={highlight.type + index}>
                    {highlight.type === 'facility_distance' &&
                      highlight.description && (
                        <span className="text-base">
                          {highlight.description} to {facilityCode}
                        </span>
                      )}
                  </Fragment>
                ))}

              <button
                onClick={() => {
                  locationRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-secondary md:text-base ml-4 mr-1"
              >
                Show Map
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 space-y-2">
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                {...props}
                className="w-5 h-5 mr-2"
              />
              <Popover
                className="flex flex-row items-center cursor-pointer"
                placement="top"
                content={unbrandedAddress}
                overlayStyle={{ maxWidth: '500px' }}
              >
                <p className={`text-base`}>More Info</p>
                <InfoCircleOutlined className="text-primary ml-2" />
              </Popover>
            </div>
            <div
              className={`flex flex-row  ${
                hasDistanceHighlight && 'justify-between'
              }`}
            >
              <FontAwesomeIcon
                icon={faPlane}
                {...props}
                className={`-rotate-90 w-5 h-5 mr-2`}
              />
              <p>
                {hasDistanceHighlight &&
                  checkedHighlights.map((highlight, index) => (
                    <Fragment key={highlight.type + index}>
                      {highlight.type === 'facility_distance' &&
                        highlight.description && (
                          <span className="text-base">
                            {highlight.description} to {facilityCode}
                          </span>
                        )}
                    </Fragment>
                  ))}
              </p>

              <button
                onClick={() => {
                  locationRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`text-secondary md:text-base ${
                  hasDistanceHighlight && 'ml-4'
                }`}
              >
                Show Map
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="md:hidden flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 mb-4 md:mb-12">
        <div className="flex flex-row items-center space-x-2">
          <Rate disabled value={review_grade} allowHalf className="text-xs" />
          <span className={`text-sm md:text-base`}>
            <span className="text-sm md:text-3xl font-semibold">
              {mapReviewStars(review_grade)}
            </span>{' '}
            / 5
          </span>

          <span className={`text-sm md:text-2xl font-semibold`}>
            {review_grade >= 4.5
              ? 'Excellent!'
              : review_grade >= 4.0
              ? 'Very Good!'
              : 'Good!'}
          </span>

          <button
            onClick={() => {
              reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-secondary text-sm md:text-base mr-1"
          >
            ({reservationStore.reviewsTotal} Reviews)
          </button>
        </div>
        {isBranded == 1 ? (
          <div className="space-y-2">
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faPlane}
                {...props}
                className="-rotate-90 w-5 h-5 mr-2"
              />
              {hasDistanceHighlight &&
                checkedHighlights.map((highlight, index) => (
                  <Fragment key={highlight.type + index}>
                    {highlight.type === 'facility_distance' &&
                      highlight.description && (
                        <span className="text-base">
                          {highlight.description} to {facilityCode}
                        </span>
                      )}
                  </Fragment>
                ))}
              {!hasDistanceHighlight && (
                <button
                  onClick={() => {
                    locationRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-secondary md:text-base ml-4 mr-1"
                >
                  Show Map
                </button>
              )}
            </div>
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                {...props}
                className="w-5 h-5 mr-2"
              />
              <p className={`text-base`}>{facilityAddress}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faPlane}
                {...props}
                className="-rotate-90 w-5 h-5 mr-2"
              />
              {hasDistanceHighlight &&
                checkedHighlights.map((highlight, index) => (
                  <Fragment key={highlight.type + index}>
                    {highlight.type === 'facility_distance' &&
                      highlight.description && (
                        <span className="text-base">
                          {highlight.description} to {facilityCode}
                        </span>
                      )}
                  </Fragment>
                ))}
              {!hasDistanceHighlight && (
                <button
                  onClick={() => {
                    locationRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-secondary md:text-base ml-4 mr-1"
                >
                  Show Map
                </button>
              )}
            </div>
            <div className="flex flex-row">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                {...props}
                className="w-5 h-5 mr-2"
              />
              <Popover
                className="flex flex-row items-center cursor-pointer"
                placement="top"
                content={unbrandedAddress}
              >
                <p className={`text-base`}>More Info</p>
                <InfoCircleOutlined className="text-primary ml-2" />
              </Popover>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
